import React from 'react';
import { Link } from 'react-router-dom';
import A1 from 'assets/A1.svg?react';
import A2 from 'assets/A2.svg?react';
import B1 from 'assets/B1.svg?react';
import B2 from 'assets/B2.svg?react';
import C1 from 'assets/C1.svg?react';
import C2 from 'assets/C2.svg?react';
import UIBox from 'components/ui/UIBox';
import UICard from 'components/ui/UICard';
import UIFlex from 'components/ui/UIFlex';
import UIText from 'components/ui/UIText';
import ProgressBar from 'components/ProgressBar';
import { CategoryTO } from 'app/types';

interface IResumeCategoryCardProps {
  record: CategoryTO;
  link: string;
  /**
   * Accent inherited from the parent /wordset row. Same role as the one
   * on CategoryCard — keeps the "Resume" card visually tied to the
   * /wordset overview it was launched from instead of switching to a
   * per-CEFR colour.
   */
  parentAccent?: string;
}

const CEFR_META: Record<
  string,
  { en: string; Icon: React.FC<React.SVGProps<SVGSVGElement>> }
> = {
  A1: { en: 'Beginner', Icon: A1 },
  A2: { en: 'Elementary', Icon: A2 },
  B1: { en: 'Intermediate', Icon: B1 },
  B2: { en: 'Upper-Intermediate', Icon: B2 },
  C1: { en: 'Advanced', Icon: C1 },
  C2: { en: 'Proficiency', Icon: C2 },
};

const DEFAULT_ACCENT = '#3B2C93';

const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(
    h.slice(2, 4),
    16,
  )}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
};

const ResumeCategoryCard: React.FC<IResumeCategoryCardProps> = ({
  record,
  link,
  parentAccent,
}) => {
  const cefr = CEFR_META[record.categoryName];
  const accent = parentAccent ?? DEFAULT_ACCENT;
  const percent = record.totalWords
    ? Math.round((record.learnedWords * 100) / record.totalWords)
    : 0;

  return (
    <Link style={{ display: 'block', flex: 'none' }} to={link}>
      <UICard
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
          maxWidth: '100%',
          p: '18px 20px',
          width: { xs: '100%', md: 420 },
        }}
      >
        <UIFlex ai={'center'} jc={'flex-start'} sx={{ flexWrap: 'nowrap', gap: 1.75 }}>
          <UIBox
            sx={{
              alignItems: 'center',
              bgcolor: hexToRgba(accent, 0.12),
              borderRadius: 1.25,
              display: 'flex',
              flex: 'none',
              height: 68,
              justifyContent: 'center',
              overflow: 'hidden',
              width: 50,
              '& svg': { width: 42, height: 'auto' },
            }}
          >
            {cefr ? (
              <cefr.Icon />
            ) : (
              <UIText
                sx={{ color: accent, fontSize: 22, fontWeight: 800 }}
              >
                {record.categoryName?.slice(0, 1).toUpperCase()}
              </UIText>
            )}
          </UIBox>

          <UIBox sx={{ flex: 1, minWidth: 0 }}>
            <UIText
              sx={{
                color: accent,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.09em',
                mb: 0.4,
                textTransform: 'uppercase',
              }}
            >
              {'Продовжити навчання'}
            </UIText>
            <UIText
              sx={{
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.title}
            </UIText>
            {cefr && (
              <UIText
                color={'primary.subtitle'}
                sx={{ fontSize: 13, mt: 0.25 }}
              >
                {cefr.en} · {record.categoryName}
              </UIText>
            )}
          </UIBox>
        </UIFlex>

        <ProgressBar
          addedValue={record.addedWords}
          learnedValue={record.learnedWords}
          totalValue={record.totalWords}
          minWidth={'100%'}
          maxWidth={'100%'}
          width={'100%'}
        />

        <UIBox
          sx={{
            alignItems: 'center',
            bgcolor: accent,
            borderRadius: 1,
            color: '#fff',
            display: 'flex',
            fontSize: 15,
            fontWeight: 700,
            gap: 1,
            height: 44,
            justifyContent: 'center',
            transition: 'opacity .15s ease',
            '&:hover': { opacity: 0.92 },
          }}
        >
          {`Продовжити з ${percent}%`}
          <UIBox component={'span'} sx={{ display: 'inline-block' }}>
            {'→'}
          </UIBox>
        </UIBox>
      </UICard>
    </Link>
  );
};

export default ResumeCategoryCard;
